// @flow
import React, { memo, createRef, PureComponent } from "react";
import { isBlank } from "voca";
import TagsInput from "react-tagsinput";
import "react-tagsinput/react-tagsinput.css";
import Autosuggest from "react-autosuggest";
import t from "common/i18n";
import SvgIcon from "../SvgIcon";
import SVG_ICONS from "~/assets/icons/icons";
import "./TaggedInput.scss";

type Suggestion = { value: string, isNew?: boolean };

type Props = {
	values: string[],
	changeHandler: Function,
	removeTag: Function,
	removeLastTag: Function,
	suggestions?: string[],
	placeholder?: string,
	renderSuggestion?: Function,
	getSuggestionValue?: Function,
	className?: string,
	filterSuggestions: Function,
	inputChangeHandler: Function,
	inputValue: string,
	reportSuggestionsContainerState: Function,
};

type AutoSuggestInputProps = Props & {
	applyInputRef: Function,
	inputProps: {
		addTag: Function,
		renderSuggestion: Function,
		filterSuggestions?: Function,
		getSuggestionValue: Function,
		ref: any,
	}
};

export const SUGGEST_CONTAINER_STATE = {
	CLOSED: 0,
	OPEN: 1,
};

const noOp = () => {
};

export class AutoSuggestInput extends PureComponent<AutoSuggestInputProps> {
	inputRef = createRef<Object>();
	
	autoSuggestRef = createRef<Object>();
	
	refocusInput = (element: ?HTMLElement) => {
		if (element) {
			element.blur();
			element.focus();
		}
	};
	
	onInputChange = (e: Object, { newValue, method }: { newValue: string, method: string }) => {
		if (method === "enter") {
			e.preventDefault();
			if (this.autoSuggestRef.current) {
				// Autosuggest doesnt close the suggestion on enter like it does for click so we have to do it :(
				this.autoSuggestRef.current.onBlur();
			}
		} else if (method !== "down" && method !== "up") {
			if (!this.props.inputValue && newValue && method === "type") {
				// need to refocus on the input when user starts typing after selection to cause the suggestions to appear
				this.refocusInput(this.inputRef.current);
			}
			
			this.props.inputChangeHandler(newValue);
		}
	};
	
	onInputKeyDown = (e: KeyboardEvent) => {
		if (e?.key === "Backspace" || e?.which === 8) {
			if (isBlank(this.props.inputValue)) {
				this.props.removeLastTag(); // start removing tags if there are any from the input once the value is empty
			}
		}
	};
	
	onInputClick = (e: { target: HTMLElement }) => {
		this.refocusInput(e.target); // re-focus to cause the suggestion to appear in case already focused
	};
	
	onSuggestionsFetchRequested = ({ value }: { value: string }) => {
		this.props.filterSuggestions(value);
		this.props.reportSuggestionsContainerState(SUGGEST_CONTAINER_STATE.OPEN);
	};
	
	renderContainer = ({ containerProps, children }: { containerProps: Object, children: Object }) => {
		if (!children) {
			setTimeout(() => {
				this.props.reportSuggestionsContainerState(SUGGEST_CONTAINER_STATE.CLOSED);
			}, 0); // break out from render flow of parent
		}
		
		return children && <div {...containerProps} className="tagged-suggestion-container">
			{children}
		</div>;
	};
	
	renderSuggestion = (suggestion: Suggestion, { isHighlighted }: { isHighlighted: boolean }) =>
		<span className={isHighlighted ? "highlight" : ""}
			  title={suggestion.value}>
    		{`${suggestion.isNew ? t("advanced_options.add_new") : ""}${suggestion.value}`}
    	</span>;
	
	onSuggestionSelected = (e: Object, { suggestion }: { suggestion: Suggestion }) => {
		this.props.inputProps.addTag(suggestion.value);
		this.props.reportSuggestionsContainerState(SUGGEST_CONTAINER_STATE.CLOSED);
		this.props.inputChangeHandler("");
		this.props.filterSuggestions("");
	};
	
	getShouldRenderSuggestions = () => true;
	
	getSuggestionValue = (suggestion: Suggestion) => (suggestion.value);
	
	getSuggestions = () => {
		const { props } = this;
		let suggestions: Suggestion[] = [];
		
		if (!isBlank(props.inputValue) && props.suggestions && !~props.suggestions.indexOf(props.inputValue)) {
			suggestions.push({ value: props.inputValue, isNew: true }); // push into suggestions so new value can be selected
		}
		
		if (props.suggestions) {
			suggestions = suggestions.concat(props.suggestions.map((s) => ({ value: s })));
		}
		
		return suggestions;
	};
	
	// eslint-disable-next-line no-unused-vars
	renderInputComponent = ({ ref, addTag, ...inputProps }: Object) => <input {...inputProps}
																			  ref={(elm) => {
																				  this.inputRef.current = elm;
																				  // expose the input dom element so it can be focused on programmatically
																				  this.props.applyInputRef(elm);
																				  ref(elm); // pass the ref to the internal component
																			  }}/>;
	
	render() {
		const { props } = this;
		const { inputProps } = props;
		
		return <Autosuggest
			ref={(elm) => {
				this.autoSuggestRef.current = elm;
				inputProps.ref(elm);
			}}
			highlightFirstSuggestion
			focusInputOnSuggestionClick={false}
			suggestions={this.getSuggestions()}
			getSuggestionValue={inputProps.getSuggestionValue || this.getSuggestionValue}
			shouldRenderSuggestions={this.getShouldRenderSuggestions}
			renderSuggestionsContainer={this.renderContainer}
			renderSuggestion={inputProps.renderSuggestion || this.renderSuggestion}
			renderInputComponent={this.renderInputComponent}
			inputProps={{
				...inputProps,
				value: props.inputValue,
				onChange: this.onInputChange,
				onKeyDown: this.onInputKeyDown,
				onClick: this.onInputClick,
				placeholder: props.placeholder,
				"data-test": "tags-input",
			}}
			onSuggestionSelected={this.onSuggestionSelected}
			onSuggestionsClearRequested={noOp}
			onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}/>;
	}
}

const TaggedInput = (props: Props) => {
	let inputElement;
	
	const renderTag = (props) => { // copied from TaggedInput lib - to add the title attribute (for ellipsised tags)
		const {
			tag, key, disabled, onRemove, classNameRemove, className,
		} = props;
		
		return <span key={key} title={tag} className={className}>
			{tag}
			{!disabled && <SvgIcon path={SVG_ICONS.CLOSE}
								   colorName="bg-link"
								   size="xxxs"
								   className={classNameRemove}
								   onClick={() => onRemove(key)}/>}
		</span>;
	};
	
	const onLayoutClick = () => // when input is taller than 1 row, its possible to click on the layout which should focus the input
		inputElement && inputElement.focus();
	
	const renderLayout = (tagComponents, inputComponent) => <span
		className="mw-100 flex flex-wrap flex-row"
		onClick={onLayoutClick}>
		{tagComponents}
		{inputComponent}
	</span>;
	
	const applyInputRef = (ref) => (inputElement = ref);
	
	return <TagsInput className={props.className}
					  renderLayout={renderLayout}
					  renderInput={(inputProps) => <AutoSuggestInput {...props}
																	 inputProps={inputProps}
																	 applyInputRef={applyInputRef}/>}
					  renderTag={renderTag}
					  onlyUnique
					  value={props.values}
					  tagProps={{
						  className: "tag bg-link bg-link-c",
						  classNameRemove: "btn-remove bg-link-c",
						  onRemove: props.removeTag,
					  }}
					  onChange={props.changeHandler}
					  removeKeys={[]}/>;
};

export default memo<Props>(TaggedInput);
